import { ChartTooltip, LineChart } from '@mantine/charts';
import { NumberFormatter } from '@mantine/core';
import { useUnit } from 'effector-react';
import { CHART_COLORS } from '../../config';
import {
	buildDashboardChartAxis,
	buildDashboardChartData,
	getDashboardMetricName,
	toDashboardChartKey,
} from '../../lib';
import { dashboardPageModel } from '../../model';
import { DashboardChartDot } from '../dashboard-chart-dot';
import cls from './dashboard-chart.module.scss';

export const DashboardChart = () => {
	const [dashboardSummary, dashboardSeries, selectedMetrics] = useUnit([
		dashboardPageModel.getDashboardSummaryQuery.$data,
		dashboardPageModel.getDashboardSeriesQuery.$data,
		dashboardPageModel.$selectedMetrics,
	]);

	const metrics = dashboardSummary?.metrics ?? [];

	const metricName = (key: string) => getDashboardMetricName(key);

	const visibleSeries = (dashboardSeries?.series ?? []).filter((item) =>
		selectedMetrics.includes(item.metric),
	);

	const lineData = buildDashboardChartData(visibleSeries);

	const chartData =
		lineData.length > 0
			? lineData
			: buildDashboardChartAxis(dashboardSummary?.from, dashboardSummary?.to);

	const chartColor = (key: string) =>
		CHART_COLORS[
			Math.max(
				metrics.findIndex((metric) => metric.key === key),
				0,
			) % CHART_COLORS.length
		];

	const chartSwatch = (color: string) =>
		color.startsWith('#') ? color : `var(--mantine-color-${color.replace('.', '-')})`;

	const chartSeries = visibleSeries.map((item) => ({
		name: toDashboardChartKey(item.metric),
		label: metricName(item.metric),
		color: chartColor(item.metric),
	}));

	if (chartData.length === 0) {
		return null;
	}

	return (
		<div className={cls.dashboardChart}>
			<LineChart
				h='55dvh'
				data={chartData}
				dataKey='date'
				series={chartSeries}
				tooltipProps={{
					content: ({ label, payload }) => (
						<ChartTooltip
							label={label}
							payload={payload}
							series={chartSeries}
							valueFormatter={(value) => (
								<NumberFormatter
									value={value}
									decimalScale={2}
								/>
							)}
						/>
					),
				}}
				curveType='monotone'
				gridAxis='xy'
				strokeDasharray='4 4'
				tickLine='none'
				withLegend={false}
				lineProps={{
					dot: <DashboardChartDot />,
					activeDot: false,
				}}
				xAxisProps={{ interval: 0 }}
				className={cls.dashboardChartPlot}
			/>
			{visibleSeries.length > 0 && (
				<div className={cls.dashboardChartLegend}>
					{visibleSeries.map((item) => (
						<div
							key={item.metric}
							className={cls.dashboardChartLegendItem}
						>
							<span
								className={cls.dashboardChartLegendSwatch}
								style={{
									background: chartSwatch(chartColor(item.metric)),
								}}
							/>
							<span>{metricName(item.metric)}</span>
						</div>
					))}
				</div>
			)}
		</div>
	);
};
